/**
 * Oatmeal Variant Tiles (Liquid-expanded) — Pin UI to data-variant-id
 *
 * NEW ARCHITECTURE ASSUMPTIONS:
 * - Liquid already renders one <li> per tile (product or variant).
 * - Variant tiles carry: <li data-variant-id="123">.
 * - We do NOT clone/duplicate anything in JS.
 *
 * JOB:
 * - Pin the gallery's visible slide to the tile variant id.
 * - Pin swatches selected state to the tile variant id.
 * - Pin the card link to ?variant=<id> (defensive; keeps PDP consistent).
 *
 * SAFE:
 * - Idempotent: re-runs after infinite scroll and doesn't thrash.
 * - Works on initial load + results-list updates.
 */

(function () {
  'use strict';

  let loaderWatchRaf = 0;
  let loaderWatchUntil = 0;
  let readyFrames = 0;
  let deferredPassQueued = false;
  let deferredScope = null;
  // Pin every tile up front. Deferring the rest to idle only bought a white
  // flash on tiles scrolled into view before idle arrived.
  const INITIAL_TILE_COUNT = Infinity;

  function qsa(sel, root) {
    return Array.from((root || document).querySelectorAll(sel));
  }

  function getVariantTiles(source) {
    if (Array.isArray(source)) return source.filter(Boolean);
    return qsa('li[data-variant-id]', source || document);
  }

  function getInitialTiles(root) {
    return getVariantTiles(root).slice(0, INITIAL_TILE_COUNT);
  }

  function getDeferredTiles(root) {
    return getVariantTiles(root).slice(INITIAL_TILE_COUNT);
  }

  function getTileVariantId(li) {
    const v = li?.dataset?.variantId || li?.getAttribute?.('data-variant-id');
    return v ? String(v) : '';
  }

  function pinCardLinks(root) {
    // Ensure each tile link includes the pinned variant id.
    getVariantTiles(root).forEach((li) => {
      const a = li.querySelector('a.product-card__link[href]');
      if (!a) return;
      const vid = getTileVariantId(li);
      if (!vid) return;

      if (a.dataset.oatPinnedFor === vid) return;
      a.dataset.oatPinnedFor = vid;

      const href = a.getAttribute('href');
      if (!href) return;

      try {
        const u = new URL(href, window.location.origin);
        u.searchParams.set('variant', vid);
        a.setAttribute('href', u.pathname + u.search + u.hash);
      } catch (_) {
        // If URL parsing fails, do nothing (avoid breaking navigation).
      }
    });
  }

  function parseVariantMediaMap(mapStr) {
    // expected: "variantId:mediaId,variantId:mediaId"
    const map = new Map();
    if (!mapStr) return map;

    for (const pair of String(mapStr).split(',')) {
      const [v, m] = pair.split(':');
      if (!v || !m) continue;
      map.set(String(v), String(m));
    }
    return map;
  }

  function pinGalleries(root) {
    // We pin galleries that expose a variant->media mapping.
    getVariantTiles(root).forEach((li) => {
      const gallery = li.querySelector('.card-gallery[data-variant-media-map]');
      if (!gallery) return;
      const vid = getTileVariantId(li);
      if (!vid) return;

      const mapStr = gallery.dataset.variantMediaMap || '';
      const map = parseVariantMediaMap(mapStr);

      const mediaId = map.get(vid) || '';
      const pc = gallery.closest('product-card');
      const slideshow = pc?.refs?.slideshow;

      if (gallery.dataset.oatPinnedFor === vid && galleryIsCorrectlyPinned(slideshow, mediaId)) {
        if (pc) {
          pc.dataset.oatDesiredVariantId = vid;
          if (mediaId) pc.dataset.oatDesiredMediaId = mediaId;
        }
        return;
      }

      if (galleryIsCorrectlyPinned(slideshow, mediaId)) {
        gallery.dataset.oatPinnedFor = vid;
        if (pc) {
          pc.dataset.oatDesiredVariantId = vid;
          if (mediaId) pc.dataset.oatDesiredMediaId = mediaId;
        }
        return;
      }

      gallery.dataset.oatPinnedFor = '';
      if (pc) {
        pc.dataset.oatDesiredVariantId = vid;
        if (mediaId) pc.dataset.oatDesiredMediaId = mediaId;
      }

      if (slideshow && mediaId) {
        try { slideshow.select({ id: mediaId }, undefined, { animate: false }); } catch (_) {}
        revealPinnedSlide(slideshow, mediaId);
        if (galleryIsCorrectlyPinned(slideshow, mediaId)) {
          gallery.dataset.oatPinnedFor = vid;
        }
      }
    });
  }

  function galleryIsCorrectlyPinned(slideshow, mediaId) {
    const targetId = String(mediaId || '');
    if (!slideshow || !targetId) return false;

    const active = slideshow.querySelector('slideshow-slide[aria-hidden="false"]');
    const target = slideshow.querySelector(`slideshow-slide[slide-id="${targetId}"]`);
    if (!active || !target) return false;

    return (
      active.getAttribute('slide-id') === targetId &&
      !target.hidden &&
      getComputedStyle(target).display !== 'none'
    );
  }

  function swatchIsCorrectlyPinned(li) {
    const vid = getTileVariantId(li);
    if (!vid) return false;

    const checked = li.querySelector('swatches-variant-picker-component input[type="radio"][data-variant-id]:checked');
    return !!checked && String(checked.dataset.variantId || '') === vid;
  }

  /**
   * Pinned is not painted: the slide flips instantly, the image still has to
   * arrive. True when there is nothing to wait for, so no card can hang the
   * overlay for ever.
   */
  function tileIsPainted(li) {
    const slide = li.querySelector('slideshow-slide[aria-hidden="false"]');
    if (!slide) return false;

    const img = slide.querySelector('img');
    if (!img) return true;

    return img.complete && img.naturalWidth > 0;
  }

  function tileIsReady(li) {
    const vid = getTileVariantId(li);
    if (!vid) return true;

    const pc = li.querySelector('product-card');
    if (!pc) return false;

    const desiredMediaId = String(pc.dataset.oatDesiredMediaId || '');
    const slideshow = pc?.refs?.slideshow;

    return (
      swatchIsCorrectlyPinned(li) &&
      galleryIsCorrectlyPinned(slideshow, desiredMediaId) &&
      tileIsPainted(li)
    );
  }

  function getCriticalVariantTiles(root) {
    const tiles = qsa('li[data-variant-id]', root);
    if (!tiles.length) return [];

    const viewportBottom = window.innerHeight || 0;
    const critical = tiles.filter((tile, index) => {
      if (index < 4) return true;
      const rect = tile.getBoundingClientRect();
      return rect.top < viewportBottom * 1.15;
    });

    return critical.length ? critical : tiles.slice(0, 4);
  }

  function finishVariantCollectionLoading() {
    document.body?.removeAttribute('data-oat-variant-collection-loading');
    document.documentElement?.removeAttribute('data-oat-loader-carry');
    try { sessionStorage.removeItem('oat-loader-carry-over'); } catch (_) {}
    window.OatLoader?.hide();
    queueDeferredRun(deferredScope || document);
  }

  function watchVariantTilesWithLoader(root) {
    const scope = root || document;
    const tiles = qsa('li[data-variant-id]', scope);
    if (!tiles.length) return;

    loaderWatchUntil = performance.now() + 5000;
    readyFrames = 0;

    if (loaderWatchRaf) {
      cancelAnimationFrame(loaderWatchRaf);
      loaderWatchRaf = 0;
    }

    const tick = () => {
      const criticalTiles = getCriticalVariantTiles(scope);
      let ready = criticalTiles.length > 0 && criticalTiles.every(tileIsReady);

      if (!ready) {
        repairCriticalTiles(criticalTiles);
        ready = criticalTiles.length > 0 && criticalTiles.every(tileIsReady);
      }

      if (ready) {
        readyFrames += 1;
        if (readyFrames >= 2) {
          finishVariantCollectionLoading();
          loaderWatchRaf = 0;
          return;
        }
      } else {
        readyFrames = 0;
      }

      if (performance.now() >= loaderWatchUntil) {
        finishVariantCollectionLoading();
        loaderWatchRaf = 0;
        return;
      }

      loaderWatchRaf = requestAnimationFrame(tick);
    };

    tick();
  }

  function revealPinnedSlide(slideshow, mediaId) {
    const targetId = String(mediaId || '');
    if (!slideshow || !targetId) return;

    const slides = qsa('slideshow-slide[slide-id]', slideshow);
    if (!slides.length) return;

    slides.forEach((slide) => {
      const isTarget = slide.getAttribute('slide-id') === targetId;
      slide.setAttribute('aria-hidden', isTarget ? 'false' : 'true');
      slide.hidden = !isTarget;
      slide.toggleAttribute('hidden', !isTarget);
      slide.style.display = isTarget ? 'flex' : 'none';
    });
  }

  function pinSwatches(root) {
  getVariantTiles(root).forEach((li) => {
    const vid = getTileVariantId(li);
    if (!vid) return;

    const wrap = li.querySelector('swatches-variant-picker-component');
    if (!wrap) return;

    const inputs = qsa('input[type="radio"][data-variant-id]', wrap);
    if (!inputs.length) return;

    if (wrap.dataset.oatPinnedFor === vid && swatchIsCorrectlyPinned(li)) return;
    wrap.dataset.oatPinnedFor = vid;

    // Clear states
    inputs.forEach((inp) => {
      inp.checked = false;
      inp.removeAttribute('checked');
      inp.dataset.previousChecked = 'false';
      inp.dataset.currentChecked = 'false';
    });

    const target = inputs.find((inp) => String(inp.dataset.variantId) === vid);
    if (!target) return;

    // Set selected
    target.checked = true;
    target.setAttribute('checked', 'checked');
    target.dataset.currentChecked = 'true';

    // Keep the pill animation sane (optional but useful)
    const fieldset = target.closest('fieldset');
    if (fieldset) {
      const w = target.parentElement?.offsetWidth || 0;
      fieldset.style.setProperty('--pill-width-current', `${w}px`);
    }

    // DO NOT DISPATCH CHANGE.
  });
  }

  // Every colour card ships the same ids, so a label hits the first card.
  function isolateSwatchIds(source) {
    getVariantTiles(source).forEach((li) => {
      const vid = getTileVariantId(li);
      if (!vid || li.dataset.oatSwatchScope === vid) return;
      li.dataset.oatSwatchScope = vid;
      qsa('input[type="radio"][id]', li).forEach((input) => {
        const label = li.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        input.id = `${input.id}--${vid}`;
        if (label) label.setAttribute('for', input.id);
        if (input.name) input.name = `${input.name}--${vid}`;
      });
    });
  }

  function run(root) {
    const scope = root || document;
    isolateSwatchIds(scope);
    pinCardLinks(scope);
    pinSwatches(scope);
    pinGalleries(scope);

    // Encourage any layout components to recalc (safe no-op if unused)
    window.dispatchEvent(new Event('resize'));
  }

  function repairCriticalTiles(tiles) {
    const blockers = (tiles || []).filter((tile) => !tileIsReady(tile));
    if (!blockers.length) return;
    pinSwatches(blockers);
    pinGalleries(blockers);
  }

  function queueDeferredRun(root) {
    const tiles = getDeferredTiles(root);
    if (!tiles.length || deferredPassQueued) return;
    deferredPassQueued = true;

    const schedule = typeof window.requestIdleCallback === 'function'
      ? (cb) => window.requestIdleCallback(cb, { timeout: 1200 })
      : (cb) => setTimeout(cb, 250);

    schedule(() => {
      run(tiles);
      deferredPassQueued = false;
    });
  }

  function scheduleRun(root) {
    const scope = root || document;
    const initialTiles = getInitialTiles(scope);
    deferredScope = scope;
    run(initialTiles);
    watchVariantTilesWithLoader(scope);
    queueMicrotask(() => run(initialTiles));
    requestAnimationFrame(() => run(initialTiles));
    setTimeout(() => run(initialTiles), 250);
    setTimeout(() => run(initialTiles), 1000);
    setTimeout(() => watchVariantTilesWithLoader(scope), 0);
    setTimeout(() => watchVariantTilesWithLoader(scope), 250);
    setTimeout(() => watchVariantTilesWithLoader(scope), 1000);
  }

  // Initial
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleRun(document));
  } else {
    scheduleRun(document);
  }

  // Infinite scroll / dynamic re-render (your theme already emits this)
  document.addEventListener('results-list:updated', (e) => {
    scheduleRun(e.target || document);
  });

  window.addEventListener('pageshow', () => {
    scheduleRun(document);
  });
})();
